import type { ActiveSettings } from "../../services/settings-service"
import type { Config } from "../models"

import { logger } from "@/lib/logger"

import { BACKEND_URI, fetchWithAuthRetry, getHeaders, type WhatsNewPayload } from "./_shared"

/**
 * QUERY: Gets application configuration (models, features, etc.)
 * @param idToken - Optional authentication token
 * @returns Configuration object
 * @category Query
 */
export async function configApi(idToken?: string): Promise<Config> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/config`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return (await response.json()) as Config
}

/**
 * QUERY: Gets "What's New" content for a locale.
 *
 * When `previewVersion` is set, the backend serves that exact version (admin
 * preview) — gated to admin_dashboard members on the server.
 *
 * @category Query
 */
export async function getWhatsNewApi(
  locale: string,
  idToken: string | undefined,
  previewVersion?: string
): Promise<WhatsNewPayload | null> {
  const params = new URLSearchParams({ locale })
  if (previewVersion) {
    params.set("version", previewVersion)
    params.set("preview", "1")
  }
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/whats-new?${params.toString()}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  if (response.status === 204) {
    return null
  }

  return (await response.json()) as WhatsNewPayload
}
/**
 * @deprecated Settings now stored in database via useUserSettings hook
 * Utility function for localStorage settings (legacy support only)
 * @category Utility
 */
export function saveSettingsToLocalStorage(settings: ActiveSettings): void {
  const settingsJson = JSON.stringify(settings)
  localStorage.setItem("activeSettings", settingsJson)
}

/**
 * @deprecated Settings now stored in database via useUserSettings hook
 * Utility function for localStorage settings (legacy support only)
 * @category Utility
 */
export function getSettingsFromLocalStorage(): ActiveSettings | null {
  const settingsJson = localStorage.getItem("activeSettings")
  if (settingsJson) {
    return JSON.parse(settingsJson) as ActiveSettings
  }
  return null
}

/**
 * @deprecated Use useUpdateUserSettings hook instead
 * MUTATION: Updates user settings (legacy - prefer React Query hook)
 * @param settings - Settings to update
 * @param idToken - Authentication token
 * @category Mutation
 */
export async function setSettingsApi(
  settings: ActiveSettings,
  idToken: string | undefined
): Promise<Response> {
  const settingsPayload = JSON.stringify({ settings })
  const bodyLength = settingsPayload.length

  // Use localstorage if settings are too large for cookies
  // or if localstorage is already being used
  // or if there are system prompts to save (first time, force localstorage)
  if (
    bodyLength > 4096 ||
    localStorage.getItem("activeSettings") ||
    settings.systemPrompts.length > 0
  ) {
    saveSettingsToLocalStorage(settings)
    return new Response("Settings too large", { status: 413 })
  }

  try {
    return await fetchWithAuthRetry(
      `${BACKEND_URI}/set-settings`,
      {
        method: "POST",
        headers: getHeaders(idToken),
        body: settingsPayload,
      },
      idToken
    )
  } catch {
    saveSettingsToLocalStorage(settings)
    return new Response("Settings too large", { status: 413 })
  }
}

/**
 * @deprecated Use useUserSettings hook instead
 * QUERY: Gets user settings (legacy - prefer React Query hook)
 * @param idToken - Authentication token
 * @returns Settings object or null
 * @category Query
 */
export async function getSettingsApi(idToken: string | undefined): Promise<ActiveSettings | null> {
  // First check localstorage
  const localSettings = getSettingsFromLocalStorage()
  if (localSettings) {
    return localSettings
  }

  // If not localstorage, try cookies
  try {
    const response = await fetchWithAuthRetry(
      `${BACKEND_URI}/get-settings`,
      {
        method: "GET",
        headers: getHeaders(idToken),
      },
      idToken
    )

    if (response.ok === false || response.status === 204) {
      return null
    }
    return (await response.json()) as ActiveSettings
  } catch (error) {
    logger.error("Error fetching settings", error as Error)
    return null
  }
}

/**
 * QUERY: Gets a system prompt by type
 * @param idToken - Authentication token
 * @param promptType - Type of prompt to retrieve
 * @returns Prompt text or null
 * @category Query
 */
export async function getSystemPromptApi(
  idToken: string | undefined,
  promptType: string
): Promise<string | null> {
  try {
    const response = await fetchWithAuthRetry(
      `${BACKEND_URI}/get-system-prompt?type=${promptType}`,
      {
        method: "GET",
        headers: getHeaders(idToken),
      },
      idToken
    )

    if (response.ok === false || response.status === 204) {
      return null
    }
    return await response.text()
  } catch (error) {
    logger.error("Error fetching system prompt", error as Error)
    return null
  }
}
